import React from 'react';

interface CoverImageEditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => void;
  coverEditFormData: {
    cover_image_url: string;
    coverImageFile: File | null;
  };
  onInputChange: (field: string, value: any) => void;
  onImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onClearImage: () => void;
  coverEditPreview: string | null;
  isSaving: boolean;
  hasChanges: () => boolean;
  error: string | null;
  successMessage: string | null;
}

const CoverImageEditDialog: React.FC<CoverImageEditDialogProps> = ({
  isOpen,
  onClose,
  onSave,
  coverEditFormData,
  onInputChange,
  onImageUpload,
  onClearImage,
  coverEditPreview,
  isSaving,
  hasChanges,
  error,
  successMessage
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-[500px] shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4 text-center">
            Chỉnh sửa ảnh bìa sách
          </h3>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-md text-sm">
              <div className="flex items-start">
                <svg
                  className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <span className="font-medium">Lỗi:</span>
                  <span className="ml-1">{error}</span>
                </div>
              </div>
            </div>
          )}

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 text-green-700 rounded-md text-sm">
              <div className="flex items-start">
                <svg
                  className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <div>
                  <span className="font-medium">Thành công:</span>
                  <span className="ml-1">{successMessage}</span>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tải lên file ảnh
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={(e) => onImageUpload(e)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={isSaving}
              />
              <p className="text-xs text-gray-500 mt-1">
                Hỗ trợ: JPG, PNG, GIF. Kích thước tối đa: 5MB
              </p>
            </div>

            {/* URL Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Hoặc nhập URL ảnh
              </label>
              <input
                type="url"
                value={coverEditFormData.cover_image_url}
                onChange={(e) => onInputChange("cover_image_url", e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                disabled={isSaving}
              />
              <p className="text-xs text-gray-500 mt-1">Nhập URL trực tiếp đến ảnh bìa</p>
            </div>

            {/* Image Preview */}
            {(coverEditPreview || coverEditFormData.cover_image_url) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Xem trước ảnh bìa
                </label>
                <div className="relative inline-block">
                  <img
                    src={coverEditPreview || coverEditFormData.cover_image_url}
                    alt="Preview ảnh bìa"
                    className="w-48 h-64 object-cover rounded-lg shadow-md border"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = "none";
                      target.nextElementSibling?.classList.remove("hidden");
                    }}
                  />
                  <div className="hidden absolute inset-0 bg-gray-100 rounded-lg flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <svg
                        className="w-16 h-16 mx-auto mb-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <p className="text-sm">Không thể hiển thị ảnh</p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={onClearImage}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors"
                    disabled={isSaving}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            )}

            {/* Note about priority */}
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="flex">
                <svg
                  className="w-4 h-4 text-blue-400 mr-2 flex-shrink-0 mt-0.5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                <p className="text-sm text-blue-700">
                  <strong>Lưu ý:</strong> File ảnh sẽ được ưu tiên hơn URL. Nếu bạn chọn cả hai,
                  file ảnh sẽ được sử dụng.
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSaving}
            >
              Hủy
            </button>
            <button
              onClick={onSave}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              disabled={isSaving || !hasChanges()}
            >
              {isSaving ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Đang lưu...
                </>
              ) : (
                <>
                  <svg
                    className="w-4 h-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                  Lưu ảnh bìa
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoverImageEditDialog;
